import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as FlagGB } from 'assets/flags/gb.svg';
import { ReactComponent as FlagNL } from 'assets/flags/nl.svg';
import { useLanguage } from 'i18n/LanguageContext';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY } from 'i18n';

const LANGUAGE_OPTIONS = [
    { code: 'en', label: 'United Kingdom', Flag: FlagGB },
    { code: 'nl', label: 'Nederland', Flag: FlagNL },
];

const stripAnyLocalePrefix = (pathname) => {
    const segment = (pathname || '/').split('/')[1];
    if (SUPPORTED_LANGUAGES.includes(segment) && segment !== DEFAULT_LANGUAGE) {
        return pathname.slice(segment.length + 1) || '/';
    }
    return pathname || '/';
};

const buildPathForLanguage = (basePath, target) => {
    if (target === DEFAULT_LANGUAGE) return basePath;
    if (basePath === '/') return `/${target}`;
    return `/${target}${basePath}`;
};

const ChevronDown = ({ className = '' }) => (
    <svg
        className={className}
        width="10"
        height="6"
        viewBox="0 0 12 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path d="M1.41 0.58L6 5.17L10.59 0.58L12 2L6 8L0 2L1.41 0.58Z" fill="currentColor" />
    </svg>
);

const FlagBadge = ({ Flag }) => (
    <span className="block w-7 h-5 overflow-hidden rounded-sm border border-neutral-200 shrink-0">
        <Flag style={{ width: '100%', height: '100%', display: 'block' }} />
    </span>
);

const LanguageSwitcher = ({ className = '', direction = 'down' }) => {
    const { language } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation('common');
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    const current = LANGUAGE_OPTIONS.find((o) => o.code === language) || LANGUAGE_OPTIONS[0];

    useEffect(() => {
        if (!open) return undefined;
        const handlePointer = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        const handleKey = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', handlePointer);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handlePointer);
            document.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    const handleSwitch = (target) => {
        setOpen(false);
        if (target === language) return;
        const basePath = stripAnyLocalePrefix(location.pathname);
        const next = buildPathForLanguage(basePath, target) + (location.search || '') + (location.hash || '');
        try {
            window.localStorage?.setItem(LANGUAGE_STORAGE_KEY, target);
        } catch (e) {
            // storage may be unavailable (private mode, etc.)
        }
        navigate(next);
    };

    const menuPosition = direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2';

    return (
        <div ref={wrapperRef} className={`relative ${className}`}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-label={t('languageSwitcher.toggle')}
                aria-haspopup="menu"
                aria-expanded={open}
                className="flex items-center gap-2 px-1 py-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
            >
                <FlagBadge Flag={current.Flag} />
                <ChevronDown className={`transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <ul
                    role="menu"
                    aria-label={t('languageSwitcher.toggle')}
                    className={`absolute right-0 ${menuPosition} z-50 min-w-[200px] bg-white text-primary border border-neutral-200 rounded-md shadow-lg py-1`}
                >
                    {LANGUAGE_OPTIONS.map(({ code, label, Flag }) => {
                        const active = code === language;
                        return (
                            <li key={code} role="none">
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => handleSwitch(code)}
                                    aria-current={active ? 'true' : undefined}
                                    className={`flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-neutral-100 ${
                                        active ? 'font-semibold' : ''
                                    }`}
                                >
                                    <FlagBadge Flag={Flag} />
                                    <span>{label}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default LanguageSwitcher;
